import React, { useState, useEffect, useCallback, memo } from "react";
import { Tabs, Tab, Container, Row, Col, Spinner } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import EmptyScreen from "../EmptyScreen";
import "./styles.scss";
import IeltsSetsPerformanceChart from "./IeltsSetsPerformanceChart";

// Register the components and scales
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const barOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: {
        usePointStyle: true,
        pointStyle: "rectRounded",
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      grid: {
        display: true,
      },
    },
  },
};

const lineOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: {
        usePointStyle: true,
        pointStyle: "circle",
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      grid: {
        display: true,
      },
      beginAtZero: true,
    },
  },
  elements: {
    line: {
      tension: 0.4, // Makes the line curved
    },
    point: {
      radius: 4,
      hitRadius: 10,
      hoverRadius: 6,
    },
  },
};

const doughnutOptions = {
  cutout: "70%",
  plugins: {
    legend: {
      display: false,
    },
  },
};

const LastWeekCharts = ({
  practiceAttempts = [],
  testAttempts = [],
  assessmentAttempts = [],
  keyType = "",
  loading,
  filter,
  onTabChange,
  hasIeltsExams,
  selectedView,
}) => {
  const [key, setKey] = useState(keyType !== "" ? keyType : "practice");
  const [practiceData, setPracticeData] = useState({
    correct: [],
    wrong: [],
    unanswered: [],
  });
  const [testData, setTestData] = useState({
    correct: [],
    wrong: [],
    unanswered: [],
  });
  const [practiceBandData, setPracticeBandData] = useState([]);
  const [testBandData, setTestBandData] = useState([]);
  const [assessmentData, setAssessmentData] = useState({
    correct: [],
    wrong: [],
    unanswered: [],
  });
  const [satScores, setSatScores] = useState({
    readingWriting: [],
    math: [],
    combined: [],
    dates: [],
  });

  // Keep key in sync with keyType prop
  useEffect(() => {
    setKey(keyType);
  }, [keyType]);

  const processData = useCallback(
    (attempts, isIelts = false) => {
      let correct, wrong, unanswered;
      const currentDate = new Date();

      switch (filter) {
        case "today":
          correct = new Array(24).fill(0);
          wrong = new Array(24).fill(0);
          unanswered = new Array(24).fill(0);
          break;
        case "week":
          correct = new Array(7).fill(0);
          wrong = new Array(7).fill(0);
          unanswered = new Array(7).fill(0);
          break;
        case "month":
          const daysInMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          ).getDate();
          correct = new Array(daysInMonth).fill(0);
          wrong = new Array(daysInMonth).fill(0);
          unanswered = new Array(daysInMonth).fill(0);
          break;
        case "year":
          correct = new Array(12).fill(0);
          wrong = new Array(12).fill(0);
          unanswered = new Array(12).fill(0);
          break;
        default:
          correct = wrong = unanswered = [];
      }

      attempts.forEach((attempt) => {
        const totalQuestions = isIelts ? attempt?.listeningAttempt?.totalQuestions + attempt?.readingAttempt?.totalQuestions : attempt?.totalQuestions;
        const answeredQuestions = isIelts ? [...(attempt?.listeningAttempt?.answeredQuestions ?? []), ...(attempt?.readingAttempt?.answeredQuestions ?? [])]?.length : attempt?.answeredQuestions?.length;

        let index;

        switch (filter) {
          case "today":
            index = new Date(isIelts ? attempt.completedAt : attempt.createdAt).getHours();
            break;
          case "week":
            index = (new Date(isIelts ? attempt.completedAt : attempt.createdAt).getDay() + 6) % 7;
            break;
          case "month":
            index = new Date(isIelts ? attempt.completedAt : attempt.createdAt).getDate() - 1;
            break;
          case "year":
            index = new Date(isIelts ? attempt.completedAt : attempt.createdAt).getMonth();
            break;
          default:
            index = 0;
        }

        if(attempt?.data?.type === 'writing' || attempt?.data?.type === 'speaking'){
          if(attempt?.gradingStatus === 'graded') {
            correct[index] += attempt?.score ?? 0
            wrong[index] += 9 - (attempt?.score ?? 0);
          } else {
            unanswered[index] += 1
          }
        } else {
          (isIelts ? [...(attempt?.listeningAttempt?.answeredQuestions ?? []), ...(attempt?.readingAttempt?.answeredQuestions ?? [])] : attempt?.answeredQuestions)?.forEach((question) => {
            if (question?.isCorrect) {
              correct[index] += 1;
            } else {
              wrong[index] += 1;
            }
          });
          unanswered[index] += totalQuestions - answeredQuestions;
        }
      });

      return { correct, wrong, unanswered };
    },
    [filter]
  );

  useEffect(() => {
    setPracticeData(processData(practiceAttempts?.filter(practice => !practice?.data?.type ) ?? []));
    setTestData(processData(testAttempts.filter(test => !test?.data?.type )));
    setPracticeBandData(practiceAttempts.filter(test => !!test?.data?.type ) ?? []);
    setTestBandData(testAttempts.filter(test => !!test?.data?.type ) ?? []);
    setAssessmentData(processData(assessmentAttempts, hasIeltsExams));
  }, [practiceAttempts, testAttempts, assessmentAttempts, processData, hasIeltsExams]);
  const getLabelsBasedOnFilter = useCallback(() => {
    switch (filter) {
      case "today":
        return Array.from({ length: 24 }, (_, i) => `${i}:00`);
      case "week":
        return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      case "month":
        const currentDate = new Date();
        const totalDaysInMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        ).getDate();
        return Array.from({ length: totalDaysInMonth }, (_, i) => `${i + 1}`);
      case "year":
        return [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
      default:
        return [];
    }
  }, [filter]);

  const createBarData = useCallback(
    (data) => ({
      labels: getLabelsBasedOnFilter(),
      datasets: [
        {
          label: "Correct",
          data: data.correct,
          backgroundColor: "#4CAF50",
        },
        {
          label: "Wrong",
          data: data.wrong,
          backgroundColor: "#E34444",
        },
        {
          label: "Unanswered",
          data: data.unanswered,
          backgroundColor: "black",
        },
      ],
    }),
    [getLabelsBasedOnFilter]
  );

  const createLineData = useCallback(
    (data) => ({
      labels: getLabelsBasedOnFilter(),
      datasets: [
        {
          label: "Correct",
          data: data.correct,
          borderColor: "#4CAF50",
          backgroundColor: "#4CAF50",
          fill: false,
        },
        {
          label: "Wrong",
          data: data.wrong,
          borderColor: "#E34444",
          backgroundColor: "#E34444",
          fill: false,
        },
        {
          label: "Unanswered",
          data: data.unanswered,
          borderColor: "#000000",
          backgroundColor: "#000000",
          fill: false,
        },
      ],
    }),
    [getLabelsBasedOnFilter]
  );

  const createDoughnutData = useCallback(
    (data) => ({
      labels: ["Correct Answers", "Wrong Answers", "Unanswered"],
      datasets: [
        {
          data: [
            data.correct.reduce((a, b) => a + b, 0),
            data.wrong.reduce((a, b) => a + b, 0),
            data.unanswered.reduce((a, b) => a + b, 0),
          ],
          backgroundColor: ["#23BD33", "#E34444", "black"],
          hoverBackgroundColor: ["#23BD33", "#E34444", "black"],
        },
      ],
    }),
    []
  );

  // Process SAT scores from assessment attempts
  const processSatScores = useCallback((attempts) => {
    if (!attempts || attempts?.length === 0) {
      return {
        readingWriting: [],
        math: [],
        combined: [],
        dates: [],
      };
    }

    const scores = {
      readingWriting: [],
      math: [],
      combined: [],
      dates: [],
    };

    // Sort attempts by date in ascending order (oldest to newest)
    const sortedAttempts = [...attempts].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    sortedAttempts?.forEach((attempt) => {
      if (!attempt?.url) return;

      try {
        const decodedData = JSON.parse(attempt.url);

        if (decodedData?.readingWritingScore && decodedData?.mathScore) {
          scores.readingWriting.push(decodedData.readingWritingScore);
          scores.math.push(decodedData.mathScore);
          scores.combined.push(decodedData.combinedSATScore);
          scores.dates.push(new Date(attempt.createdAt).toLocaleDateString());
        }
      } catch (error) {
        console.error("Error parsing SAT scores:", error);
      }
    });

    return scores;
  }, []);

  // Update SAT scores when assessment attempts change
  useEffect(() => {
    if(!hasIeltsExams) {
      setSatScores(processSatScores(assessmentAttempts));
    }
  }, [assessmentAttempts, processSatScores, hasIeltsExams]);
  const createSatScoreData = useCallback(
    () => ({
      labels: satScores.dates,
      datasets: [
        {
          label: "Reading & Writing",
          data: satScores.readingWriting,
          borderColor: "#4CAF50",
          backgroundColor: "#4CAF50",
          fill: false,
          tension: 0.4,
          pointStyle: "circle",
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 2,
          order: 3, // Higher order means it will be drawn on top
        },
        {
          label: "Math",
          data: satScores.math,
          borderColor: "#2196F3",
          backgroundColor: "#2196F3",
          fill: false,
          tension: 0.4,
          pointStyle: "rectRot",
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 2,
          order: 2,
        },
        {
          label: "Total Score",
          data: satScores.combined,
          borderColor: "#9C27B0",
          backgroundColor: "#9C27B0",
          fill: false,
          tension: 0.4,
          pointStyle: "triangle",
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 2,
          order: 1,
        },
      ],
    }),
    [satScores]
  );

  const satScoreOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
        intersect: false,
        mode: "nearest",
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        grid: {
          display: true,
        },
        min: 0,
        max: 1600,
        title: {
          display: true,
          text: "Score",
        },
        ticks: {
          stepSize: 200,
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hitRadius: 10,
      },
    },
    interaction: {
      intersect: false,
      mode: "nearest",
    },
  };

  const renderCharts = (data, chartType = "bar", isAccessment = false) => {
    if (loading) {
      return null;
    }

    if (chartType === "sat") {
      if ((!hasIeltsExams && satScores?.dates?.length === 0) || (hasIeltsExams && assessmentAttempts?.length === 0)) {
        return (
          <EmptyScreen
            heading={`No ${hasIeltsExams ? 'IELTS' : 'SAT'} attempts available`}
            subHeading={`Please attempt full-length ${hasIeltsExams ? 'IELTS' : 'SAT'} exams to view your progress`}
          />
        );
      }

      return (
        <>
          <Col md={12} xs={12}>
            <Line data={createSatScoreData()} options={satScoreOptions} />
          </Col>
          <Col md={12} xs={12} className="mt-4">
            <div className="score-summary">
              <h5 className="text-center mb-4">Latest Scores</h5>
              <div className="score-cards">
                <div className="score-card">
                  <div className="score-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div className="score-content">
                    <span className="score-label">Reading & Writing</span>
                    <div className="score-values">
                      <div className="latest-score">
                        <span className="score-value">
                          {satScores.readingWriting[
                            satScores.readingWriting?.length - 1
                          ] || "N/A"}
                        </span>
                        <span className="score-label-small">Latest</span>
                      </div>
                      <div className="initial-score">
                        <span className="score-value">
                          {satScores.readingWriting[0] || "N/A"}
                        </span>
                        <span className="score-label-small">First Attempt</span>
                      </div>
                    </div>
                    {satScores.readingWriting?.length > 1 && (
                      <div className="progress-indicator">
                        <span
                          className={`trend ${
                            satScores.readingWriting[
                              satScores.readingWriting?.length - 1
                            ] > satScores.readingWriting[0]
                              ? "up"
                              : "down"
                          }`}
                        >
                          {satScores.readingWriting[
                            satScores.readingWriting?.length - 1
                          ] > satScores.readingWriting[0]
                            ? "↑"
                            : "↓"}
                        </span>
                        <span className="progress-value">
                          {Math.abs(
                            satScores.readingWriting[
                              satScores.readingWriting?.length - 1
                            ] - satScores.readingWriting[0]
                          )}{" "}
                          points
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="score-card">
                  <div className="score-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2v20M2 12h20" />
                    </svg>
                  </div>
                  <div className="score-content">
                    <span className="score-label">Math</span>
                    <div className="score-values">
                      <div className="latest-score">
                        <span className="score-value">
                          {satScores.math[satScores.math?.length - 1] || "N/A"}
                        </span>
                        <span className="score-label-small">Latest</span>
                      </div>
                      <div className="initial-score">
                        <span className="score-value">
                          {satScores.math[0] || "N/A"}
                        </span>
                        <span className="score-label-small">First Attempt</span>
                      </div>
                    </div>
                    {satScores.math?.length > 1 && (
                      <div className="progress-indicator">
                        <span
                          className={`trend ${
                            satScores.math[satScores.math?.length - 1] >
                            satScores.math[0]
                              ? "up"
                              : "down"
                          }`}
                        >
                          {satScores.math[satScores.math?.length - 1] >
                          satScores.math[0]
                            ? "↑"
                            : "↓"}
                        </span>
                        <span className="progress-value">
                          {Math.abs(
                            satScores.math[satScores.math?.length - 1] -
                              satScores.math[0]
                          )}{" "}
                          points
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="score-card">
                  <div className="score-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <div className="score-content">
                    <span className="score-label">Total Score</span>
                    <div className="score-values">
                      <div className="latest-score">
                        <span className="score-value">
                          {satScores.combined[satScores?.combined?.length - 1] ||
                            "N/A"}
                        </span>
                        <span className="score-label-small">Latest</span>
                      </div>
                      <div className="initial-score">
                        <span className="score-value">
                          {satScores.combined[0] || "N/A"}
                        </span>
                        <span className="score-label-small">First Attempt</span>
                      </div>
                    </div>
                    {satScores.combined?.length > 1 && (
                      <div className="progress-indicator">
                        <span
                          className={`trend ${
                            satScores.combined[satScores.combined?.length - 1] >
                            satScores.combined[0]
                              ? "up"
                              : "down"
                          }`}
                        >
                          {satScores.combined[satScores.combined?.length - 1] >
                          satScores.combined[0]
                            ? "↑"
                            : "↓"}
                        </span>
                        <span className="progress-value">
                          {Math.abs(
                            satScores.combined[satScores.combined?.length - 1] -
                              satScores.combined[0]
                          )}{" "}
                          points
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </>
      );
    }

    const totalC = data.correct.reduce((a, b) => a + b, 0)
    const totalW = data.wrong.reduce((a, b) => a + b, 0)

    const totalCorrect = totalC;
    const totalWrong = totalW;
    const totalUnanswered = data.unanswered.reduce((a, b) => a + b, 0);
    const total = totalCorrect + totalWrong + totalUnanswered;

    if(total === 0 && hasIeltsExams && isAccessment === true) {
      return (
        <EmptyScreen
            heading={`No ${hasIeltsExams ? 'IELTS' : 'SAT'} attempts available`}
            subHeading={`Please attempt full-length ${hasIeltsExams ? 'IELTS' : 'SAT'} exams to view your progress`}
          />
      )
    }

    if (total === 0) {
      return (
        <EmptyScreen
          heading="No data available"
          subHeading="Please attempt tests or practice sets to view your progress"
        />
      );
    }

    return (
      <>
        <Col md={8} xs={12}>
          {chartType === "bar" ? (
            <Bar data={createBarData(data)} options={barOptions} />
          ) : (
            <Line data={createLineData(data)} options={lineOptions} />
          )}
        </Col>
        <Col md={4} xs={12} className="d-flex flex-column align-items-center">
          <div
            className="totals"
            style={{ marginBottom: "10px", textAlign: "center" }}
          >
            <p>
              <strong>Total Correct</strong> {totalCorrect}
            </p>
            <p>
              <strong>Total Incorrect:</strong> {totalWrong}
            </p>
            <p>
              <strong>Total Unanswered:</strong> {totalUnanswered}
            </p>
          </div>
          <div className="doughnut-chart">
            <Doughnut
              data={createDoughnutData(data)}
              options={doughnutOptions}
            />
          </div>
        </Col>
      </>
    );
  };

  const renderChartsOrLoader = (data, chartType = "bar", isAccessment = false) => {
    if (loading) {
      return (
        <div className="loader-container">
          <Spinner animation="border" />
        </div>
      );
    }
    return renderCharts(data, chartType, isAccessment);
  };

  return (
    <Container>
      <Tabs
        id="controlled-tab-example"
        activeKey={key}
        onSelect={(k) => {
          setKey(k);
          onTabChange?.(k);
        }}
        className="mb-3"
      >
        <Tab eventKey="practice" title="Practice">
          <Row className="mt-4">
            {selectedView === 'score' ? renderChartsOrLoader(practiceData, "bar") : <IeltsSetsPerformanceChart data={practiceBandData} filter={filter} /> }
          </Row>
        </Tab>
        <Tab eventKey="test" title="Test">
          <Row className="mt-4">{selectedView === 'score' ? renderChartsOrLoader(testData, "bar") : <IeltsSetsPerformanceChart data={testBandData} filter={filter} /> }</Row>
        </Tab>
        <Tab eventKey="assessment" title="Full Length Exams">
          <Row className="mt-4">
            {hasIeltsExams ? renderChartsOrLoader(assessmentData, "bar", true) : renderChartsOrLoader(assessmentData, "sat")}
          </Row>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default memo(LastWeekCharts);
